import { isApolloError } from '@apollo/client';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { tracking } from 'App/Tracking';
import { Errors } from 'Shared/Errors';
import {
  RegistrationMethod,
  SubRoutes,
  TRACKED_REGISTRATION_METHODS,
  VERIFICATION_STEP_NAMES,
  TrackedAuthType,
  TrackedEntryPoint,
  TrackedFailureReason,
} from '../constants';
import { VerificationTracker, VerificationTrackingProps } from '../types';

const getFailureReason = (
  reasonOrError: TrackedFailureReason | Error,
): TrackedFailureReason => {
  if (!(reasonOrError instanceof Error)) return reasonOrError;

  return isApolloError(reasonOrError) &&
    reasonOrError.graphQLErrors[0]?.name === Errors.RateLimitError
    ? TrackedFailureReason.RateLimited
    : TrackedFailureReason.Unknown;
};

/**
 * Exported for its own tests: it is the only seam that exercises the event
 * payloads without a router or Apollo. Build it via the hook, not directly.
 */
export const createVerificationTracker = (
  props: VerificationTrackingProps,
): VerificationTracker => ({
  stepViewed: stepName =>
    tracking.track('account-verification-step-viewed', {
      ...props,
      stepName,
    }),

  stepCompleted: stepName =>
    tracking.track('account-verification-step-completed', {
      ...props,
      stepName,
    }),

  attempted: outcome =>
    tracking.track('account-verification-attempted', {
      ...props,
      ...(outcome && { outcome }),
    }),

  failed: reasonOrError =>
    tracking.track('account-verification-failed', {
      ...props,
      reason: getFailureReason(reasonOrError),
    }),
});

interface UseVerificationTracking {
  membershipId: string | null;
  registrationMethods: RegistrationMethod[];
  registrationMethod:
    | RegistrationMethod.OPEN_EMAIL
    | RegistrationMethod.CLOSED_EMPLOYEE_ID;
}

export const useVerificationTracking = ({
  membershipId,
  registrationMethods,
  registrationMethod,
}: UseVerificationTracking): {
  manualTracker: VerificationTracker;
  membershipTracker: VerificationTracker;
  onStepViewed(route: SubRoutes): void;
  onStepCompleted(route: SubRoutes): void;
} => {
  const location = useLocation();

  const entryPoint =
    location.state?.entryPoint ??
    (membershipId ? TrackedEntryPoint.MembershipId : TrackedEntryPoint.Manual);

  const authType = registrationMethods.includes(RegistrationMethod.SSO)
    ? TrackedAuthType.MixedMode
    : TrackedAuthType.Credentials;

  const membershipTracker = useMemo(
    () => createVerificationTracker({ entryPoint, authType }),
    [entryPoint, authType],
  );

  const manualTracker = useMemo(
    () =>
      createVerificationTracker({
        entryPoint,
        authType,
        registrationMethod: TRACKED_REGISTRATION_METHODS[registrationMethod],
      }),
    [entryPoint, authType, registrationMethod],
  );

  const lastViewedStep = useRef<SubRoutes>();

  const onStepViewed = useCallback(
    (route: SubRoutes) => {
      const stepName = VERIFICATION_STEP_NAMES[route];

      if (!stepName || lastViewedStep.current === route) return;

      lastViewedStep.current = route;
      manualTracker.stepViewed(stepName);
    },
    [manualTracker],
  );

  const onStepCompleted = useCallback(
    (route: SubRoutes) => {
      const stepName = VERIFICATION_STEP_NAMES[route];

      if (stepName) manualTracker.stepCompleted(stepName);
    },
    [manualTracker],
  );

  return { manualTracker, membershipTracker, onStepViewed, onStepCompleted };
};
